import { useLazyQuery } from '@apollo/client';
import { Autocomplete, CircularProgress, TextField, type AutocompleteProps } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useSettingIds } from '~/components/settings';
import { useAppContext } from '~/contexts';
import type { DeviceGroupAutocomplete__DeviceGroup as DeviceGroup } from './DeviceGroupAutocomplete.generated';
import { DeviceGroupAutocompleteDocument } from './DeviceGroupAutocomplete.generated';

export interface DeviceGroupAutocompleteProps
  extends Omit<
    AutocompleteProps<DeviceGroup | null, false, false, false>,
    'options' | 'onChange' | 'renderInput' | 'defaultValue' | 'freeSolo' | 'multiple'
  > {
  onChange: (group: DeviceGroup | null) => void;
  value: DeviceGroup | null;
}

export const DeviceGroupAutocomplete = ({
  onChange,
  value,
  ...props
}: DeviceGroupAutocompleteProps) => {
  const { t } = useTranslation();
  // Inside a settings row the row's label names the input.
  const ids = useSettingIds();
  const { currentNetwork } = useAppContext();
  const [open, setOpen] = useState(false);

  const [query, { called, data, loading }] = useLazyQuery(DeviceGroupAutocompleteDocument);

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (!called) {
      void query({ variables: { networkId: currentNetwork.id } });
    }
  }, [called, currentNetwork.id, query]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, newValue: DeviceGroup | null) => {
      onChange(newValue);
    },
    [onChange],
  );

  return (
    <Autocomplete
      id={ids?.inputId}
      {...props}
      fullWidth
      autoHighlight
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      loading={loading}
      onChange={handleChange}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      options={data?.network?.allDeviceGroups ?? []}
      renderInput={(params) => (
        <TextField
          {...namedByPlaceholder(params, t('common.placeholders.select_group'), ids != null)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    aria-label={t('common.status.loading')}
                    color="inherit"
                    size={20}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      value={value}
    />
  );
};
